"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { Mark } from "@/components/services/shared/demo-parts";

import { useShowcaseShellHeight } from "@/components/services/shared/useShowcaseShellHeight";

import { CcLeadMessage } from "./CcLeadMessage";
import type { LeadGenDemoCopy } from "./lead-gen-demo-copy";

type CcLeadShowcaseProps = {
  copy: LeadGenDemoCopy["showcase"];
};

const LEAD_SHOWCASE_ENTER_MS = 560;

/**
 * Before/after of outreach messages: a typical AI mass message next to the
 * hyper-personal message we'd send, with the signals it was built on. Same
 * layout and motion as the AI automation showcase (intro, sticky pills, two
 * panels side by side).
 */
export function CcLeadShowcase({ copy }: CcLeadShowcaseProps) {
  const [activeId, setActiveId] = useState(copy.cases[0]?.id ?? "");
  const [entering, setEntering] = useState(false);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const introShellRef = useRef<HTMLDivElement>(null);
  const introInnerRef = useRef<HTMLDivElement>(null);
  const flowsShellRef = useRef<HTMLDivElement>(null);
  const flowsInnerRef = useRef<HTMLDivElement>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const active = copy.cases.find((item) => item.id === activeId) ?? copy.cases[0];

  const selectCase = useCallback(
    (id: string) => {
      if (id === activeId) return;
      setActiveId(id);
      if (prefersReducedMotion) return;
      setEntering(true);
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = setTimeout(() => setEntering(false), LEAD_SHOWCASE_ENTER_MS);
    },
    [activeId, prefersReducedMotion],
  );

  useShowcaseShellHeight(
    introShellRef,
    introInnerRef,
    prefersReducedMotion,
    activeId,
    active?.title,
    active?.text,
    active?.points,
  );

  useShowcaseShellHeight(
    flowsShellRef,
    flowsInnerRef,
    prefersReducedMotion,
    activeId,
    active?.before,
    active?.after,
  );

  useEffect(
    () => () => {
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    },
    [],
  );

  if (!active) return null;

  const copyBlocks = (
    <div className="cc-web-showcase__copies">
      {copy.cases.map((item) => {
        const isActive = item.id === active.id;
        return (
          <div
            key={item.id}
            className="cc-web-showcase__copy"
            data-active={isActive}
            aria-hidden={!isActive}
            inert={!isActive}
          >
            <CcSanjayaEyebrow theme="on-subtle">{item.name}</CcSanjayaEyebrow>
            <h3 className="cc-web-showcase__title">{item.title}</h3>
            <p className="cc-web-showcase__text">{item.text}</p>
            <ul className="cc-web-showcase__points">
              {item.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const casePills = (
    <div className="cc-auto-showcase__case-switch-shell">
      <div
        className="cc-web-showcase__switch cc-auto-showcase__case-switch"
        role="group"
        aria-label={copy.tablistLabel}
      >
        {copy.cases.map((item) => (
          <button
            key={item.id}
            type="button"
            className="cc-web-showcase__pill"
            aria-pressed={item.id === active.id}
            onClick={() => selectCase(item.id)}
          >
            <Mark logo={item.logo} badge={item.badge} />
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section
      className="cc-section cc-web-showcase-section cc-ai-showcase-section cc-auto-showcase-section cc-lead-showcase-section"
      aria-labelledby="cc-lead-showcase-title"
      data-cc-nav="light"
      {...devSectionAttr("lead-showcase")}
    >
      <div className="cc-container">
        <h2 id="cc-lead-showcase-title" className="cc-web-showcase__sr">
          {copy.sectionTitle}
        </h2>
        <div className="cc-web-showcase cc-auto-showcase">
          <div className="cc-auto-showcase__intro-row">
            <div className="cc-auto-showcase__intro-copy-shell" ref={introShellRef}>
              <div
                ref={introInnerRef}
                className="cc-auto-showcase__intro-copy"
                data-entering={entering ? "true" : undefined}
              >
                {copyBlocks}
              </div>
            </div>
            {casePills}
          </div>

          <figure
            id="cc-lead-showcase-messages"
            className="cc-web-showcase__media cc-auto-showcase__demo"
          >
            <div className="cc-auto-showcase__flows-shell" ref={flowsShellRef}>
              <div
                className="cc-auto-showcase__flows"
                ref={flowsInnerRef}
                data-entering={entering ? "true" : undefined}
              >
                {(["before", "after"] as const).map((state) => (
                  <div key={state} className="cc-auto-showcase__flow-panel">
                    <CcLeadMessage
                      key={`${active.id}-${state}`}
                      channel={active.channel}
                      logo={active.logo}
                      badge={active.badge}
                      stateLabel={state === "before" ? copy.beforeLabel : copy.afterLabel}
                      stateVariant={state}
                      to={active.to}
                      message={active[state]}
                      labels={{
                        to: copy.toLabel,
                        subject: copy.subjectLabel,
                        chips: state === "before" ? copy.beforeChipsLabel : copy.afterChipsLabel,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}
